/**
 * נתיבים שאפשר לראות בלי סשן. כל השאר — כולל "/" ו-"" (כשה-URL הוא בדיוק
 * ה-basename, למשל /new בלי סלאש) — דורשים התחברות. בלי זה ניתוק על /new
 * משאיר את המעטפת בלי הפניה לדף ההתחברות (HAT-1092).
 */
const PUBLIC_PREFIXES = ['/login', '/register', '/forgot-password', '/renew-forgotten-password', '/donate', '/open-fund', '/privacy-policy', '/regulations'] as const;

import { NEW_VIEWER_BASENAME, NEW_VIEWER_LOGIN_HREF, redirectBareNewBase } from './newViewerBase';

export { NEW_VIEWER_BASENAME, NEW_VIEWER_BASENAME_SLASH, NEW_VIEWER_LOGIN_HREF, redirectBareNewBase } from './newViewerBase';

/** Client-side: keep a logged-in tab off bare `/new` so the next refresh still hits the SPA. */
export const canonicalizeBareNewBasename = (): void => {
  if (typeof window === 'undefined') return;
  const dest = redirectBareNewBase(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  if (!dest) return;
  window.history.replaceState(window.history.state, '', dest);
};

export const isSessionPublicPath = (pathname: string): boolean => {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return PUBLIC_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`));
};

/** נתיב בתוך האפליקציה אחרי ה-basename, או null אם לא תחת /new. */
export const appPathFromWindowPathname = (pathname: string): string | null => {
  if (pathname === NEW_VIEWER_BASENAME || pathname === `${NEW_VIEWER_BASENAME}/`) return '';
  if (pathname.startsWith(`${NEW_VIEWER_BASENAME}/`)) return pathname.slice(NEW_VIEWER_BASENAME.length);
  return null;
};

export const shouldHardRedirectToLogin = (windowPathname: string): boolean => {
  const appPath = appPathFromWindowPathname(windowPathname);
  if (appPath === null) return false;
  return !isSessionPublicPath(appPath);
};

let hardRedirectIssued = false;
let explicitLogout = false;

/**
 * יציאה מכוונת: ההתחברות הבאה - אולי של מישהו אחר - מתחילה מדף הבית ולא מהעמוד
 * שבו התנתקו.
 */
export const markExplicitLogout = (): void => {
  explicitLogout = true;
};

/**
 * HAT-1395: העמוד שהסשן נגמר עליו, כדי שההתחברות תחזיר אליו. קישור ממייל שנפתח
 * בלי סשן הגיע אחרי ההתחברות לדף הבית, והבקשה שהמייל הצביע עליה אבדה. null לדף
 * הבית עצמו ולעמודים ציבוריים - לשם ההתחברות לא צריכה להחזיר.
 */
export const returnToFromLocation = (pathname: string, search = ''): string | null => {
  const appPath = appPathFromWindowPathname(pathname);
  if (!appPath || appPath === '/' || isSessionPublicPath(appPath)) return null;
  return `${appPath}${search}`;
};

/**
 * רק נתיב בתוך האפליקציה. returnTo מגיע מהכתובת, וכל אחד יכול לשלוח קישור
 * להתחברות שלנו - "//evil.com" או "/\evil.com" הם כתובות של אתר אחר.
 */
export const safeReturnTo = (value: string | null): string | null => {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  if (isSessionPublicPath(value.split(/[?#]/)[0])) return null;
  return value;
};

/**
 * <Navigate> לא משנה את ה-URL כשהדפדפן עומד על /new בלי מקטע (pathname ריק
 * אחרי basename). החלפה קשיחה מבטיחה שנגיע לטופס ההתחברות.
 * Latch למסמך: StrictMode / כמה קוראים לא יעשו replace חוזר שיטען מחדש את /login.
 */
export const redirectClearedSessionToLogin = (): void => {
  if (typeof window === 'undefined') return;
  if (hardRedirectIssued) return;
  if (!shouldHardRedirectToLogin(window.location.pathname)) return;
  hardRedirectIssued = true;
  const returnTo = explicitLogout ? null : returnToFromLocation(window.location.pathname, window.location.search);
  window.location.replace(returnTo ? `${NEW_VIEWER_LOGIN_HREF}?returnTo=${encodeURIComponent(returnTo)}` : NEW_VIEWER_LOGIN_HREF);
};

export const resetHardRedirectLatch = (): void => {
  hardRedirectIssued = false;
  explicitLogout = false;
};
